// src/services/dashboard.service.js

import apiClient from "../utils/apiClient";

/* =====================================================
   RESPONSE NORMALIZER
   Handles:
   - New API format
   - Legacy API format
===================================================== */

const normalizeApiResponse = (response) => {
  const payload = response?.data;

  if (!payload) return {};

  // NEW STANDARDIZED FORMAT
  // { success: true, data: {...} }
  if (payload.success && payload.data !== undefined) {
    return payload.data;
  }

  // LEGACY FORMAT
  return payload;
};

const dashboardService = {
  /* ===============================
     CORE DASHBOARD
  =============================== */
  getDashboardData: async (role = "admin") => {
    try {
      const endpointMap = {
        admin: "/dashboard/summary",
        sales_manager: "/dashboard/summary",
        advisor: "/dashboard/summary",
        team_lead: "/dashboard/summary",
      };

      const endpoint = endpointMap[role] || endpointMap.admin;

      const res = await apiClient.get(endpoint);

      return normalizeApiResponse(res);

    } catch (err) {
      console.error("Dashboard load failed:", err);

      return {
        revenue: {
          total: 0,
          collected: 0,
        },
        bookings: {
          total: 0,
        },
        leads: {
          total: 0,
          converted: 0,
          conversionRate: 0,
        },
      };
    }
  },

  /* ===============================
     KPI METRICS
  =============================== */
  getKpiMetrics: async () => {
    try {
      const [bookingStats, tripStats] = await Promise.all([
        apiClient
          .get("/bookings/stats")
          .catch(() => ({ data: {} })),

        Promise.all([
          apiClient
            .get("/dashboard/trips-starting-today/count")
            .catch(() => ({ data: {} })),

          apiClient
            .get("/dashboard/trips-ending-today/count")
            .catch(() => ({ data: {} })),

          apiClient
            .get("/dashboard/total-trips/count")
            .catch(() => ({ data: {} })),
        ]),
      ]);

      return {
        bookings: normalizeApiResponse(bookingStats),

trips: {
  starting: Number(
    tripStats?.[0]?.data?.data?.value ?? 0
  ),

  ending: Number(
    tripStats?.[1]?.data?.data?.value ?? 0
  ),

  total: Number(
    tripStats?.[2]?.data?.data?.value ?? 0
  ),
},

      };

    } catch (err) {
      console.error("KPI metrics error:", err);

      return {
        bookings: {},
        trips: {
          starting: 0,
          ending: 0,
          total: 0,
        },
      };
    }
  },

  /* ===============================
     LIVE OPERATIONS
  =============================== */
  getLiveOperations: async () => {
    try {
      const [liveTrips, urgentTrips] = await Promise.all([
        apiClient
          .get("/dashboard/live-trips")
          .catch(() => ({ data: [] })),

        apiClient
          .get("/dashboard/urgent-trips")
          .catch(() => ({ data: [] })),
      ]);

      return {
        liveTrips:
          liveTrips?.data?.data ||
          liveTrips?.data ||
          [],

        urgentTrips:
          urgentTrips?.data?.data ||
          urgentTrips?.data ||
          [],
      };

    } catch (err) {
      console.error("Live operations error:", err);

      return {
        liveTrips: [],
        urgentTrips: [],
      };
    }
  },

  /* ===============================
     OPERATIONS DASHBOARD
  =============================== */
  getOperationsDashboard: async () => {
    try {
      const res = await apiClient.get(
        "/operations/dashboard"
      );

      return normalizeApiResponse(res);

    } catch (err) {
      console.error(
        "Operations dashboard error:",
        err
      );

      return {
        trips: [],
        stats: {
          totalTrips: 0,
          pendingPayments: 0,
          pendingHotelBookings: 0,
          pendingTransportBookings: 0,
          readyTrips: 0,
          totalRevenue: 0,
        },
      };
    }
  },

  /* ===============================
     FULL DASHBOARD
  =============================== */
  loadFullDashboard: async function (role = "admin") {
    try {
      const [core, kpis, operations] = await Promise.all([
        this.getDashboardData(role),
        this.getKpiMetrics(),
        this.getLiveOperations(),
      ]);

      return {
        core,
        kpis,
        operations,
      };

    } catch (err) {
      console.error("Full dashboard load failed:", err);

      return {
        core: {
          revenue: {
            total: 0,
            collected: 0,
          },
          bookings: {
            total: 0,
          },
          leads: {
            total: 0,
            converted: 0,
            conversionRate: 0,
          },
        },

        kpis: {
          bookings: {},
          trips: {
            starting: 0,
            ending: 0,
            total: 0,
          },
        },

        operations: {
          liveTrips: [],
          urgentTrips: [],
        },
      };
    }
  },
};

export default dashboardService;
